import React, { FC, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  TextField,
  Tooltip,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DialogTextActionButton } from "../../Base/DialogTextActionButton";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";

import {
  changeMatchStatus,
  changeMatchTable,
  closeBlockedMatchModal,
  getBlockedMatchModalParams,
  closeOccupiedTableModal,
  getOccupiedTableModalParams,
  getMatchsLoading,
  listenToMatchs,
  forceMatchEnd,
  selectMatchesWithBlockInfo,
} from "../../../features/matchs/matchsSlice";
import { useAppDispatch, useAppSelector } from "../../../store";
import { MatchWithBlockInfo } from "../../../types";
import { getTournoi } from "../../../features/tournoi/tournoiSlice";
import { Tournoi } from "../../../types/Tournoi";
import { getMatchRowBackgroundColor, getMatchRowSx } from "./matchRowColors";

type SortColumn = {
  orderBy: string; // chemin Firestore ou champ à trier
  order: "asc" | "desc";
};

const columns = [
  {
    id: "tableau",
    label: "Tableau",
    orderBy: "tableau.label",
    sortable: true,
  },
  { id: "tour", label: "Tour", orderBy: "tour.label", sortable: true },
  {
    id: "joueur1",
    label: "Joueur 1",
    orderBy: "matchsPlayerParticipations.0.players.name",
    sortable: true,
  },
  {
    id: "joueur2",
    label: "Joueur 2",
    orderBy: "matchsPlayerParticipations.1.players.name",
    sortable: true,
  },
  {
    id: "joueur3",
    label: "Joueur 3",
    orderBy: "matchsPlayerParticipations.2.players.name",
    sortable: true,
  },
  {
    id: "dateCreation",
    label: "Création",
    orderBy: "dateCreation",
    sortable: true,
  },
  {
    id: "dateLancement",
    label: "Lancement",
    orderBy: "dateLancement",
    sortable: true,
  },
  { id: "duree", label: "Durée", orderBy: "dateLancement", sortable: true },
  { id: "statut", label: "Statut", orderBy: "status", sortable: true },
  { id: "tableId", label: "Table", orderBy: "tableId", sortable: true },
  { id: "action", label: "Action", sortable: false },
  { id: "forceEnd", label: "", sortable: false },
];

function getValueByPath(obj: any, path: string) {
  return path.split(".").reduce((acc, part) => {
    if (acc === undefined || acc === null) return undefined;
    // gérer index numérique ex: "matchsPlayerParticipations.0"
    if (/^\d+$/.test(part)) {
      return Array.isArray(acc) ? acc[parseInt(part, 10)] : undefined;
    }
    return acc[part];
  }, obj);
}

function compareValues(a: any, b: any): number {
  // Regrouper undefined/null à la fin, les considérer égaux entre eux
  const aIsNil = a === undefined || a === null;
  const bIsNil = b === undefined || b === null;

  if (aIsNil && bIsNil) return 0; // égaux entre eux => passer au critère suivant
  if (aIsNil) return 1; // a va après b
  if (bIsNil) return -1; // a avant b

  // Sinon comparer normalement selon le type
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  }
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  // Ajoute d'autres types si besoin

  // Cas générique
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
}

function multiSort(
  a: any,
  b: any,
  sortCriteria: { path: string; direction: "asc" | "desc" }[]
) {
  for (const { path, direction } of sortCriteria) {
    const valA = getValueByPath(a, path);
    const valB = getValueByPath(b, path);
    const cmp = compareValues(valA, valB);
    if (cmp !== 0) {
      return direction === "asc" ? cmp : -cmp;
    }
    // si cmp === 0, passer au critère suivant
  }
  return 0;
}

// Helper pour savoir si un match doit être alerté et le type d'alerte
function getMatchAlertInfo(
  match: MatchWithBlockInfo,
  alertThresholds: Tournoi["alertThresholds"]
): null | { type: "cree" | "enCours"; minutes: number; seuil: number } {
  if (!alertThresholds) return null;
  const nbJoueurs = match.matchsPlayerParticipations?.length;
  if (nbJoueurs !== 2 && nbJoueurs !== 3) return null;
  const nbJoueursKey = String(nbJoueurs ?? "");
  const now = Date.now();
  if (["CREE", "DOUBLON", "LIBERE"].includes(match.status || "")) {
    const seuil = alertThresholds.cree?.[nbJoueursKey];
    if (!seuil || !match.dateCreation) return null;
    const minutes = (now - match.dateCreation.getTime()) / 60000;
    if (minutes > seuil)
      return { type: "cree", minutes: Math.floor(minutes), seuil };
    return null;
  }
  if ((match.status || "") === "ENCOURS") {
    const seuil = alertThresholds.enCours?.[nbJoueursKey];
    if (!seuil || !match.dateLancement) return null;
    const minutes = (now - match.dateLancement.getTime()) / 60000;
    if (minutes > seuil)
      return { type: "enCours", minutes: Math.floor(minutes), seuil };
    return null;
  }
  return null;
}

type MatchStatusCountKey = "ENCOURS" | "CREE" | "DOUBLON" | "LIBERE";

const STATUS_SUMMARY_ITEMS: Array<{
  status: MatchStatusCountKey;
  label: string;
  dotColor: string;
}> = [
  { status: "ENCOURS", label: "encours", dotColor: "#4ade80" },
  { status: "CREE", label: "créés", dotColor: "#cbd5e1" },
  { status: "LIBERE", label: "libérés", dotColor: "#22d3ee" },
  { status: "DOUBLON", label: "doublons", dotColor: "#e879f9" },
];

type MatchStatusFilter = "all" | "encours" | "a_lancer" | "bloques";

const MATCH_STATUS_FILTERS: Array<{ id: MatchStatusFilter; label: string }> = [
  { id: "all", label: "Tous" },
  { id: "encours", label: "En cours" },
  { id: "a_lancer", label: "À lancer" },
  { id: "bloques", label: "Bloqués" },
];

function matchPassesStatusFilter(
  match: MatchWithBlockInfo,
  filter: MatchStatusFilter
): boolean {
  switch (filter) {
    case "encours":
      return match.status === "ENCOURS";
    case "a_lancer":
      return match.status === "CREE" || match.status === "LIBERE";
    case "bloques":
      return match.status === "DOUBLON";
    default:
      return true;
  }
}

function normalizePlayerSearchQuery(query: string): string {
  return query.trim().toLocaleLowerCase("fr");
}

function matchPassesPlayerSearch(
  match: MatchWithBlockInfo,
  searchQuery: string
): boolean {
  const normalized = normalizePlayerSearchQuery(searchQuery);
  if (!normalized) {
    return true;
  }

  return match.matchsPlayerParticipations.some((participation) => {
    const player = participation.players;
    if (!player) {
      return false;
    }

    const name = player.name.toLocaleLowerCase("fr");
    const surname = player.surname.toLocaleLowerCase("fr");
    const fullName = `${name} ${surname}`;
    const fullNameReversed = `${surname} ${name}`;

    return (
      name.includes(normalized) ||
      surname.includes(normalized) ||
      fullName.includes(normalized) ||
      fullNameReversed.includes(normalized)
    );
  });
}

function getMatchStatusCounts(matchs: MatchWithBlockInfo[]) {
  const result: Record<MatchStatusCountKey, number> = {
    ENCOURS: 0,
    CREE: 0,
    DOUBLON: 0,
    LIBERE: 0,
  };

  for (const match of matchs) {
    const status = match.status;
    if (
      status === "ENCOURS" ||
      status === "CREE" ||
      status === "DOUBLON" ||
      status === "LIBERE"
    ) {
      result[status] += 1;
    }
  }

  return result;
}

function MatchsListToolbar({
  matchs,
  filter,
  onFilterChange,
  playerSearch,
  onPlayerSearchChange,
}: {
  matchs: MatchWithBlockInfo[];
  filter: MatchStatusFilter;
  onFilterChange: (filter: MatchStatusFilter) => void;
  playerSearch: string;
  onPlayerSearchChange: (value: string) => void;
}) {
  const counts = React.useMemo(() => getMatchStatusCounts(matchs), [matchs]);
  const visibleSummaryItems = STATUS_SUMMARY_ITEMS.filter(
    ({ status }) => counts[status] > 0
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        mb: 0.5,
        minHeight: 28,
        flexWrap: "wrap",
      }}
    >
      <ToggleButtonGroup
        size="small"
        exclusive
        value={filter}
        onChange={(_event, value: MatchStatusFilter | null) => {
          if (value) {
            onFilterChange(value);
          }
        }}
        aria-label="Filtrer les matchs par statut"
        sx={{
          flexShrink: 0,
          "& .MuiToggleButton-root": {
            py: 0.15,
            px: 1,
            fontSize: "0.75rem",
            lineHeight: 1.4,
            textTransform: "none",
            color: "text.secondary",
            borderColor: "divider",
            "&.Mui-selected": {
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.common.white
                  : theme.palette.primary.contrastText,
              bgcolor: "primary.main",
              borderColor: "primary.main",
              fontWeight: 700,
              "&:hover": {
                bgcolor: "primary.dark",
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? theme.palette.common.white
                    : theme.palette.primary.contrastText,
              },
            },
          },
        }}
      >
        {MATCH_STATUS_FILTERS.map(({ id, label }) => (
          <ToggleButton key={id} value={id}>
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <TextField
        size="small"
        value={playerSearch}
        onChange={(event) => onPlayerSearchChange(event.target.value)}
        placeholder="Rechercher un joueur…"
        aria-label="Rechercher un joueur"
        sx={{
          width: { xs: "100%", sm: 168 },
          flexShrink: 0,
          "& .MuiInputBase-root": {
            fontSize: "0.8125rem",
            height: 28,
          },
          "& .MuiInputBase-input": {
            py: 0.5,
          },
        }}
      />

      {visibleSummaryItems.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            gap: 1.25,
            flex: 1,
            minWidth: 0,
          }}
          aria-label="Répartition des matchs par statut"
        >
          {visibleSummaryItems.map(({ status, label, dotColor }) => (
            <Typography
              key={status}
              variant="caption"
              color="text.secondary"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                letterSpacing: 0.2,
                whiteSpace: "nowrap",
                color: "text.primary",
                opacity: 0.9,
              }}
            >
              <Box
                component="span"
                aria-hidden
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: dotColor,
                  flexShrink: 0,
                  boxShadow: (t) =>
                    t.palette.mode === "dark"
                      ? `0 0 0 1px ${t.palette.common.black}`
                      : "none",
                }}
              />
              {counts[status]} {label}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

function parseMatchTableInput(
  match: MatchWithBlockInfo,
  tableInputs: Record<string, string>
): number | undefined {
  const raw = tableInputs[match.id!] ?? match.tableId?.toString() ?? "";
  if (raw === "") {
    return undefined;
  }
  const intVal = parseInt(raw, 10);
  if (Number.isNaN(intVal) || intVal <= 0) {
    return undefined;
  }
  return intVal;
}

type MatchsListProps = {
  highlightedMatchId?: string | null;
};

function parseStatusFilterParam(value: string | null): MatchStatusFilter {
  if (
    value === "encours" ||
    value === "a_lancer" ||
    value === "bloques" ||
    value === "all"
  ) {
    return value;
  }
  return "all";
}

const MatchsList: FC<MatchsListProps> = ({ highlightedMatchId = null }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const matchs = useAppSelector(selectMatchesWithBlockInfo);
  const loading = useAppSelector(getMatchsLoading);
  const blockedMatchModalParams = useAppSelector(getBlockedMatchModalParams);
  const occupiedTableModalParams = useAppSelector(getOccupiedTableModalParams);
  const tournoi = useAppSelector(getTournoi);
  const defaultAlertThresholds = {
    cree: { "2": 60, "3": 20 },
    enCours: { "2": 50, "3": 100 },
  };
  const alertThresholds = {
    cree: {
      ...defaultAlertThresholds.cree,
      ...(tournoi?.alertThresholds?.cree || {}),
    },
    enCours: {
      ...defaultAlertThresholds.enCours,
      ...(tournoi?.alertThresholds?.enCours || {}),
    },
  };

  const urlMatchId = searchParams.get("matchId");
  const urlTableId = searchParams.get("tableId");
  const urlTableauId = searchParams.get("tableauId");
  const urlTourId = searchParams.get("tourId");
  const urlStatus = parseStatusFilterParam(searchParams.get("status"));
  const effectiveHighlightId = highlightedMatchId ?? urlMatchId;

  const [sortColumns, setSortColumns] = React.useState<SortColumn[]>([]);
  const [statusFilter, setStatusFilter] =
    React.useState<MatchStatusFilter>(urlStatus);
  const [playerSearch, setPlayerSearch] = React.useState("");
  const rowRefs = React.useRef<Record<string, HTMLTableRowElement | null>>({});

  React.useEffect(() => {
    setStatusFilter(urlStatus);
  }, [urlStatus]);

  React.useEffect(() => {
    if (!effectiveHighlightId) {
      return;
    }
    const row = rowRefs.current[effectiveHighlightId];
    if (row) {
      row.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [effectiveHighlightId, matchs]);

  const hasDeepLinkFilters = Boolean(
    urlMatchId || urlTableId || urlTableauId || urlTourId || urlStatus !== "all"
  );

  const clearDeepLinkFilters = () => {
    setSearchParams({}, { replace: true });
    setStatusFilter("all");
  };

  const filteredMatchs = React.useMemo(
    () =>
      matchs.filter((match) => {
        if (!matchPassesStatusFilter(match, statusFilter)) return false;
        if (!matchPassesPlayerSearch(match, playerSearch)) return false;
        if (urlMatchId && String(match.id) !== urlMatchId) return false;
        if (urlTableId && String(match.tableId ?? "") !== urlTableId) {
          return false;
        }
        if (urlTableauId && String(match.tableauId ?? "") !== urlTableauId) {
          return false;
        }
        if (urlTourId && String(match.tourId ?? "") !== urlTourId) {
          return false;
        }
        return true;
      }),
    [
      matchs,
      statusFilter,
      playerSearch,
      urlMatchId,
      urlTableId,
      urlTableauId,
      urlTourId,
    ]
  );

  const emptyListMessage = React.useMemo(() => {
    if (matchs.length === 0) {
      return "Aucun match";
    }
    const hasSearch = normalizePlayerSearchQuery(playerSearch).length > 0;
    const hasStatusFilter = statusFilter !== "all";
    if (hasSearch && hasStatusFilter) {
      return "Aucun match pour ce filtre et cette recherche";
    }
    if (hasSearch) {
      return "Aucun match pour cette recherche";
    }
    return "Aucun match pour ce filtre";
  }, [matchs.length, playerSearch, statusFilter]);

  const sortedMatchs = React.useMemo(() => {
    if (sortColumns.length === 0) return filteredMatchs;

    const sortCriteria = sortColumns.map(({ orderBy, order }) => ({
      path: orderBy,
      direction: order,
    }));

    return [...filteredMatchs].sort((a, b) => multiSort(a, b, sortCriteria));
  }, [filteredMatchs, sortColumns]);

  const handleRequestSort = (property: string) => {
    // Trouve la colonne par son id
    const column = columns.find((col) => col.id === property);
    if (!column || !column.orderBy) return;

    setSortColumns((prev) => {
      const existingIndex = prev.findIndex((c) => c.orderBy === column.orderBy);
      if (existingIndex === -1) {
        // Ajoute la colonne en ascendant par défaut
        return [...prev, { orderBy: column.orderBy, order: "asc" }];
      } else {
        // Inverse l'ordre ou supprime la colonne si déjà desc
        const updated = [...prev];
        const currentOrder = updated[existingIndex].order;
        if (currentOrder === "asc") {
          updated[existingIndex].order = "desc";
          return updated;
        } else {
          // supprime la colonne du tri si on clique une 2e fois en desc
          updated.splice(existingIndex, 1);
          return updated;
        }
      }
    });
  };

  const getSortDirection = (col: (typeof columns)[number]) => {
    if (!col.sortable || !col.orderBy) return false;
    const colSort = sortColumns.find((c) => c.orderBy === col.orderBy);
    return colSort ? colSort.order : false;
  };

  useEffect(() => {
    // Démarrage du listener temps réel
    const unsubscribe = dispatch(listenToMatchs());
    return () => unsubscribe();
  }, [dispatch]);

  const [tableInputs, setTableInputs] = React.useState<Record<string, string>>(
    {}
  );
  const [matchToForceEnd, setMatchToForceEnd] =
    React.useState<MatchWithBlockInfo | null>(null);

  const getPlayerName = (match: MatchWithBlockInfo, index: number) => {
    if (!match || !match.matchsPlayerParticipations || index < 0 || index > 3) {
      return "";
    }
    const player = match.matchsPlayerParticipations?.[index]?.players;
    return player ? `${player.name} ${player.surname}` : "";
  };

  const isPlayerBlocking = (
    match: MatchWithBlockInfo,
    playerId: string
  ): boolean => {
    if (!playerId) return false;
    // Si match en doublon, check si joueur est bloquant
    if (match.status === "DOUBLON" && match.blockedByPlayers) {
      if (match.blockedByPlayers.some((bp) => bp.player.id === playerId)) {
        return true;
      }
    }

    // Si match en cours, check si joueur bloque un autre match (bloquéPlayers)
    if (match.status === "ENCOURS" && match.blockedPlayers) {
      if (match.blockedPlayers.some((bp) => bp.player.id === playerId)) {
        return true;
      }
    }

    return false;
  };

  const getMatchDuration = (match: MatchWithBlockInfo) => {
    if (match.status !== "ENCOURS" || !match.dateLancement) return "";
    const now = Date.now();
    const launch = match.dateLancement.getTime();
    return `${Math.floor((now - launch) / 1000 / 60)} min`;
  };

  const handleTableChangeLocal = (matchId: string, val: string) => {
    // Autoriser vide pour vider le champ
    if (val === "") {
      setTableInputs((prev) => ({ ...prev, [matchId]: "" }));
      return;
    }
    // Accepter uniquement entiers > 0
    const intVal = parseInt(val, 10);
    if (!isNaN(intVal) && intVal > 0) {
      setTableInputs((prev) => ({ ...prev, [matchId]: intVal.toString() }));
    }
  };

  const handleTableChangeCommit = async (match: MatchWithBlockInfo) => {
    const tableId = parseMatchTableInput(match, tableInputs);
    const previousTableId =
      typeof match.tableId === "number" && Number.isFinite(match.tableId)
        ? match.tableId
        : undefined;
    // Champ vidé → supprimer tableId en base (pas seulement l’état local).
    if (tableId === undefined) {
      if (previousTableId == null) {
        return undefined;
      }
      await dispatch(
        changeMatchTable({
          matchId: match.id!,
          tableId: null,
        })
      );
      return undefined;
    }
    if (tableId === previousTableId) {
      return tableId;
    }
    await dispatch(
      changeMatchTable({
        matchId: match.id!,
        tableId,
      })
    );
    return tableId;
  };

  const handleMatchActionClick = async (match: MatchWithBlockInfo) => {
    if (match.status === "ENCOURS") {
      dispatch(changeMatchStatus(match));
      return;
    }

    const tableId = await handleTableChangeCommit(match);
    if (!tableId) {
      return;
    }

    dispatch(changeMatchStatus({ ...match, tableId }));
  };

  const handleTableInputKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    match: MatchWithBlockInfo
  ) => {
    if (event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    void handleTableChangeCommit(match).then(() => {
      (event.target as HTMLElement).blur();
    });
  };

  return (
    <>
      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" p={2}>
          <CircularProgress />
          <Typography>Chargement des matchs...</Typography>
        </Box>
      ) : (
        <>
          <MatchsListToolbar
            matchs={matchs}
            filter={statusFilter}
            onFilterChange={(next) => {
              setStatusFilter(next);
              setSearchParams(
                (prev) => {
                  const nextParams = new URLSearchParams(prev);
                  if (next === "all") nextParams.delete("status");
                  else nextParams.set("status", next);
                  return nextParams;
                },
                { replace: true }
              );
            }}
            playerSearch={playerSearch}
            onPlayerSearchChange={setPlayerSearch}
          />
          {hasDeepLinkFilters && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                mb: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Filtre Occupation :
              </Typography>
              {urlMatchId && (
                <Chip size="small" label={`Match ${urlMatchId.slice(0, 6)}…`} />
              )}
              {urlTableId && (
                <Chip size="small" label={`Table ${urlTableId}`} />
              )}
              {urlTableauId && (
                <Chip size="small" label={`Tableau ${urlTableauId.slice(0, 6)}…`} />
              )}
              {urlTourId && (
                <Chip size="small" label={`Tour ${urlTourId}`} />
              )}
              {urlStatus !== "all" && (
                <Chip size="small" label={`Statut ${urlStatus}`} />
              )}
              <Button size="small" onClick={clearDeepLinkFilters}>
                Effacer
              </Button>
            </Box>
          )}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              width: "100%",
              maxHeight: "calc(100vh - 284px)",
              overflow: "auto",
              marginBottom: 2,
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
            }}
          >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map(({ id, label, sortable }) => (
                  <TableCell
                    key={id}
                    onClick={() => sortable && handleRequestSort(id)}
                    sx={{
                      cursor: sortable ? "pointer" : "default",
                      userSelect: "none",
                      bgcolor: "background.paper",
                      color: "text.primary",
                      borderBottomColor: "divider",
                      fontWeight: 600,
                    }}
                    sortDirection={
                      getSortDirection(columns.find((c) => c.id === id)!) ||
                      false
                    }
                  >
                    <Typography
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        userSelect: "none",
                        color: "inherit",
                      }}
                      variant="body2"
                    >
                      {label}
                      {(() => {
                        const sortDir = getSortDirection(
                          columns.find((c) => c.id === id)!
                        );
                        if (!sortDir) return null;
                        return sortDir === "asc" ? " ▲" : " ▼";
                      })()}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedMatchs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ py: 2 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {emptyListMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedMatchs.map((match) => {
                const isHighlighted = match.id === effectiveHighlightId;
                return (
                  <TableRow
                    key={match.id}
                    ref={(node) => {
                      if (match.id) {
                        rowRefs.current[match.id] = node;
                      }
                    }}
                    sx={{
                      ...getMatchRowSx(match.status, theme, {
                        highlighted: isHighlighted,
                      }),
                      ...(isHighlighted && {
                        animation: "matchCreatedFlash 1.6s ease-out",
                        "@keyframes matchCreatedFlash": {
                          "0%": {
                            boxShadow: "inset 0 0 0 3px rgba(199, 154, 97, 1)",
                          },
                          "70%": {
                            boxShadow: "inset 0 0 0 2px rgba(199, 154, 97, 0.5)",
                          },
                          "100%": {
                            boxShadow: "inset 0 0 0 0 transparent",
                          },
                        },
                      }),
                    }}
                  >
                    <TableCell>
                      {match.tableau?.name && match.tableau?.label
                        ? `${match.tableau.name} - ${match.tableau.label}`
                        : match.tableau?.label ?? ""}
                    </TableCell>
                    <TableCell>{match.tour?.label ?? ""}</TableCell>
                    {[0, 1, 2].map((i) => {
                      const playerParticipation =
                        match.matchsPlayerParticipations?.[i];
                      const playerName = playerParticipation?.players
                        ? `${playerParticipation.players.name} ${playerParticipation.players.surname}`
                        : "";
                      const isBlocking =
                        playerParticipation &&
                        isPlayerBlocking(match, playerParticipation.playersId);
                      return (
                        <TableCell
                          key={i}
                          sx={{
                            color: isBlocking
                              ? (t) =>
                                  t.palette.mode === "dark"
                                    ? "#ff8a80"
                                    : "error.main"
                              : "text.primary",
                            fontWeight: isBlocking ? 700 : 500,
                          }}
                        >
                          {playerName}
                        </TableCell>
                      );
                    })}
                    <TableCell>
                      {match.dateCreation?.toLocaleTimeString("fr-FR")}
                    </TableCell>
                    <TableCell>
                      {match.dateLancement?.toLocaleTimeString("fr-FR")}
                    </TableCell>
                    <TableCell>{getMatchDuration(match)}</TableCell>
                    <TableCell>
                      {(() => {
                        const alertInfo = getMatchAlertInfo(
                          match,
                          alertThresholds
                        );
                        if (!alertInfo) return null;
                        let message = "";
                        if (alertInfo.type === "cree") {
                          message = `Ce match n'a pas été lancé depuis plus de ${alertInfo.seuil} min (${alertInfo.minutes} min)`;
                        } else {
                          message = `Ce match est en cours depuis plus de ${alertInfo.seuil} min (${alertInfo.minutes} min)`;
                        }
                        return (
                          <Tooltip title={message}>
                            <WarningAmberOutlinedIcon
                              color="warning"
                              sx={{ mr: 0.5, verticalAlign: "middle" }}
                            />
                          </Tooltip>
                        );
                      })()}
                      {match.status === "DOUBLON" && match.blockedByPlayers ? (
                        <Tooltip
                          title={
                            <List sx={{ p: 1, m: 0 }}>
                              {match.blockedByPlayers.map(
                                ({ player, match }, i) => (
                                  <ListItem key={i} sx={{ fontSize: "0.8rem" }}>
                                    {player.name} {player.surname} bloque sur la
                                    table {match.tableId}
                                  </ListItem>
                                )
                              )}
                            </List>
                          }
                          arrow
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "help",
                              color: (t) =>
                                t.palette.mode === "dark"
                                  ? "#ff8a80"
                                  : "error.main",
                              fontWeight: "bold",
                              gap: 0.5, // espace entre texte et picto
                              userSelect: "none",
                            }}
                          >
                            <Typography component="span">DOUBLON</Typography>
                            <Typography
                              component="span"
                              aria-label="Warning icon"
                              role="img"
                            >
                              ⚠️
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        match.status
                      )}
                    </TableCell>

                    <TableCell
                      sx={{
                        padding: "4px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <TextField
                        size="small"
                        type="number"
                        value={
                          tableInputs[match.id!] ??
                          match.tableId?.toString() ??
                          ""
                        }
                        onChange={(e) =>
                          handleTableChangeLocal(match.id!, e.target.value)
                        }
                        onBlur={() => {
                          void handleTableChangeCommit(match);
                        }}
                        onKeyDown={(event) =>
                          handleTableInputKeyDown(event, match)
                        }
                        inputProps={{
                          min: 1,
                          step: 1,
                          style: { textAlign: "center", padding: "4px 6px" },
                        }}
                        sx={{
                          width: "4rem",
                          height: "32px",
                          padding: "0 6px",
                          fontSize: "0.875rem",
                          outline: "none",
                          textAlign: "center",
                          backgroundColor: "transparent",
                          lineHeight: "32px",
                          boxShadow: "none",
                          marginBottom: "0px",
                          "& .MuiOutlinedInput-root": {
                            color: "text.primary",
                            bgcolor: (t) =>
                              t.palette.mode === "dark"
                                ? "rgba(0,0,0,0.25)"
                                : "rgba(255,255,255,0.55)",
                            "& fieldset": {
                              borderColor: "divider",
                            },
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "4px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <Button
                        className="match-action-button"
                        variant="contained"
                        size="small"
                        color={
                          match.status === "ENCOURS" ? "secondary" : "primary"
                        }
                        disabled={
                          match.status !== "ENCOURS" &&
                          parseMatchTableInput(match, tableInputs) === undefined
                        }
                        onClick={() => {
                          void handleMatchActionClick(match);
                        }}
                        sx={{
                          "&.match-action-button::after": { display: "none" },
                          "&.match-action-button:hover::after": {
                            display: "none",
                          },
                          "&.match-action-button:hover": {
                            backgroundColor:
                              match.status === "ENCOURS"
                                ? (theme) => theme.palette.secondary.main
                                : (theme) => theme.palette.primary.main,
                            color:
                              match.status === "ENCOURS"
                                ? (theme) => theme.palette.text.primary
                                : (theme) => theme.palette.primary.contrastText,
                            boxShadow: "none",
                            filter: "none",
                            transform: "none !important",
                          },
                          "&.match-action-button:focus-visible": {
                            outline: "none",
                            boxShadow: "none",
                          },
                          "&.match-action-button:active": {
                            backgroundColor:
                              match.status === "ENCOURS"
                                ? (theme) => theme.palette.secondary.main
                                : (theme) => theme.palette.primary.main,
                            boxShadow: "none",
                            transform: "none !important",
                          },
                        }}
                      >
                        {match.status === "ENCOURS" ? "TERMINER" : "LANCER"}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {match.status !== "ENCOURS" && (
                        <StopCircleOutlinedIcon
                          color="error"
                          fontSize="small"
                          sx={{ cursor: "pointer" }}
                          onClick={() => setMatchToForceEnd(match)}
                          titleAccess="Forcer la fin du match"
                        />
                      )}
                      {match.bloquant && (
                        <Tooltip title="Match bloquant" arrow placement="top">
                          <BlockOutlinedIcon
                            color="error"
                            fontSize="small"
                            sx={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      {/* Dialogs */}
      <Dialog
        open={!!blockedMatchModalParams}
        onClose={() => dispatch(closeBlockedMatchModal())}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberOutlinedIcon color="warning" />
          <Typography variant="h6">Conflit de joueurs détecté</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Typography variant="body1" gutterBottom>
            Le match que vous venez de lancer a bien été créé.
            <br />
            <strong>
              Cependant, les matchs suivants sont désormais en doublon à cause
              de conflits de joueurs&nbsp;:
            </strong>
          </Typography>
          {blockedMatchModalParams?.occupiedTableId != null && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              La table {blockedMatchModalParams.occupiedTableId} est également
              occupée par un autre match en cours. Même sans ce conflit de
              joueurs, le lancement sur cette table serait impossible.
            </Typography>
          )}
          <List dense>
            {(blockedMatchModalParams?.blockingPlayers ?? []).map(
              (bp: any, idx: number) => {
                const match = bp.match;
                const joueurs = match.matchsPlayerParticipations
                  .map((mpp: any) =>
                    mpp.players
                      ? `${mpp.players.surname} ${mpp.players.name}`
                      : ""
                  )
                  .filter(Boolean)
                  .join(", ");
                return (
                  <ListItem
                    key={match.id}
                    disableGutters
                    sx={{
                      flexDirection: "column",
                      alignItems: "flex-start",
                      mb: 1,
                      width: "100%",
                    }}
                  >
                    <ListItemText
                      primary={
                        <>
                          {match.tableId != null ? (
                            <>
                              <strong>Table {match.tableId}</strong> —{" "}
                              <span
                                style={{ color: "#b71c1c", fontWeight: 600 }}
                              >
                                DOUBLON
                              </span>
                            </>
                          ) : (
                            <span style={{ color: "#b71c1c", fontWeight: 600 }}>
                              DOUBLON
                            </span>
                          )}
                        </>
                      }
                      secondary={
                        <>
                          <span>Joueurs du match bloqué : {joueurs}</span>
                        </>
                      }
                      primaryTypographyProps={{ fontWeight: "bold" }}
                      secondaryTypographyProps={{
                        fontSize: "0.95rem",
                        color: "text.secondary",
                      }}
                    />
                  </ListItem>
                );
              }
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <DialogTextActionButton
            onClick={() => dispatch(closeBlockedMatchModal())}
          >
            Fermer
          </DialogTextActionButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!occupiedTableModalParams}
        onClose={() => dispatch(closeOccupiedTableModal())}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BlockOutlinedIcon color="error" />
          <Typography variant="h6">Table occupée</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Typography variant="body1">
            La table <strong>{occupiedTableModalParams?.blockedTable}</strong>{" "}
            est déjà occupée. Veuillez terminer le match en cours avant d'en
            lancer un nouveau.
          </Typography>
        </DialogContent>
        <DialogActions>
          <DialogTextActionButton
            onClick={() => dispatch(closeOccupiedTableModal())}
          >
            Fermer
          </DialogTextActionButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!matchToForceEnd}
        onClose={() => setMatchToForceEnd(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberOutlinedIcon color="warning" />
          <Typography variant="h6">Confirmer la fin du match</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Typography variant="body1">
            Êtes-vous sûr de vouloir forcer la fin du match entre{" "}
            <strong>{getPlayerName(matchToForceEnd!, 0)}</strong> et{" "}
            <strong>{getPlayerName(matchToForceEnd!, 1)}</strong> ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <DialogTextActionButton onClick={() => setMatchToForceEnd(null)}>
            Annuler
          </DialogTextActionButton>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (!matchToForceEnd) return;
              await dispatch(forceMatchEnd({ matchId: matchToForceEnd.id! }));
              setMatchToForceEnd(null);
            }}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MatchsList;
